import { useEffect, useState, useRef } from "react";
import { officerAPI, departmentAPI, positionAPI, officeAPI } from "../services/api";
import { StatusBadge, Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import {
  MdPerson, MdWarning, MdBadge, MdCake,
  MdLocationOn, MdHome, MdWork, MdAccountBalance, MdCalendarToday,
  MdAttachMoney, MdSchool, MdScience, MdAssignment, MdNotes,
  MdPhone, MdEmail, MdAccessTime, MdEdit, MdLock, MdCheckCircle, MdDelete,
} from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_LABEL = {
  ACTIVE:           "កំពុងបម្រើការ",
  INACTIVE:         "មិនសកម្ម",
  RETIRED:          "ចូលនិវត្តន៍",
  SUSPENDED:        "ផ្អាកការងារ",
  ON_LEAVE:         "ឈប់សម្រាក",
  PROBATION:        "សាកល្បង",
  RESIGNED:         "បានលាឈប់",
  CONTRACT_EXPIRED: "កិច្ចសន្យាផុត",
};

const GENDER_LABEL = { M:"ប្រុស", F:"ស្រី", MONK:"បព្វជិត" };

const getStatus = (obj) => obj?.status || obj?.statusCode || "";
const getId = (obj) => obj?.officerId || obj?.id;
const getPositionName = (obj) =>
  obj?.positionName || obj?.position?.positionName || obj?.position?.name || "";
const getDeptName = (obj) =>
  obj?.departmentName || obj?.department?.departmentName || obj?.department?.name || "";
const getPositionId = (obj) =>
  obj?.positionId || obj?.position?.positionId || obj?.position?.id;
const getDeptId = (obj) =>
  obj?.departmentId || obj?.department?.departmentId || obj?.department?.id;
const getOfficeName = (obj) =>
  obj?.officeName || obj?.office?.officeName || obj?.office?.name || "";
const getOfficeId = (obj) =>
  obj?.officeId || obj?.office?.officeId || obj?.office?.id;
const API_ORIGIN = "https://ginbecc-backend.onrender.com";
const toImageUrl = (raw) => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  if (/^https?:\/\//.test(raw)) return raw;
  if (raw.startsWith("/")) return API_ORIGIN + raw;
  return null;
};

const toDisplay = (iso) => {
  if (!iso) return "—";
  const p = iso.slice(0,10).split("-");
  if (p.length !== 3) return iso;
  return `${p[2]}-${p[1]}-${p[0]}`;
};

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

const errStyle = (field, errors) => errors[field]
  ? { ...inputStyle, border:"1.5px solid #EF4444",
      background:"#FFF5F5", boxShadow:"0 0 0 2px rgba(239,68,68,0.15)" }
  : inputStyle;

const ERROR_STYLE = `
  @keyframes errorPulse {
    0%   { transform: scale(1);    opacity:0.5; }
    50%  { transform: scale(1.01); opacity:1;   }
    100% { transform: scale(1);    opacity:1;   }
  }
`;

function SkeletonRow() {
  return (
    <tr>
      {Array(9).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function ErrMsg({ field, errors }) {
  if (!errors[field]) return null;
  return (
    <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
      <MdWarning size={13} /> {errors[field]}
    </p>
  );
}

function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:12, color:MUTED,
                    marginBottom:4, fontFamily:KH }}>
      {children}
      {required && <span style={{ color:"#EF4444", marginLeft:3 }}>*</span>}
    </label>
  );
}

function FieldBox({ children }) {
  return <div style={{ marginBottom:14 }}>{children}</div>;
}

function SectionTitle({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                margin:"16px 0 10px", borderBottom:`1px solid ${BORDER}`,
                paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10,
                  background:BG, padding:"9px 14px", borderRadius:8, marginBottom:6 }}>
      <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{icon}</span>
      <div>
        <p style={{ fontSize:10, color:MUTED, marginBottom:2, fontFamily:KH }}>{label}</p>
        <div style={{ fontSize:13, fontWeight:500, color:"var(--text)", fontFamily:KH }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default function Staff() {
  const { t } = useLanguage();
  const [list,         setList]         = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [positions,    setPositions]    = useState([]);
  const [offices,      setOffices]      = useState([]);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deptFilter,   setDeptFilter]   = useState("");
  const [modal,        setModal]        = useState(null);
  const [form,         setForm]         = useState({});
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [errors,       setErrors]       = useState({});
  const errorRef       = useRef(null);
  const nameKhRef      = useRef(null);
  const genderRef      = useRef(null);
  const statusRef      = useRef(null);
  const positionRef    = useRef(null);
  const departmentRef  = useRef(null);
  const officeRef      = useRef(null);
  const joinDateRef    = useRef(null);
  const phoneRef       = useRef(null);
  const emailRef       = useRef(null);
  const [editId,       setEditId]       = useState(null);
  const [originalCode, setOriginalCode] = useState("");
  const [deleteId,     setDeleteId]     = useState(null);
  const [statusChange, setStatusChange] = useState(null);
  const [toast,        setToast]        = useState(null);
  const [err,          setErr]          = useState(null);
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(25);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalItems,   setTotalItems]   = useState(0);
  const [imageFile,    setImageFile]    = useState(null);
  const [lightbox,     setLightbox]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await officerAPI.getAll(page, pageSize,
        deptFilter || undefined, statusFilter || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    departmentAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setDepartments((Array.isArray(d) ? d : []).map(x => ({
        ...x, id: x.departmentId ?? x.id,
        departmentName: x.departmentName ?? x.name,
      })));
    }).catch(() => {});
    positionAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setPositions((Array.isArray(d) ? d : []).map(x => ({
        ...x, id: x.positionId ?? x.id,
        positionName: x.positionName ?? x.name,
      })));
    }).catch(() => {});
    officeAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setOffices((Array.isArray(d) ? d : []).map(x => ({
        ...x, id: x.officeId ?? x.id,
        officeName: x.officeName ?? x.name,
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, pageSize, statusFilter, deptFilter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const requiredFields = [
      { key:"fullNameKh",   msg:t("សូមបញ្ចូលឈ្មោះពេញ"),      ref:nameKhRef,     empty:!form.fullNameKh?.trim() },
      { key:"gender",       msg:t("សូមជ្រើសរើសភេទ"),           ref:genderRef,     empty:!form.gender },
      { key:"positionId",   msg:t("សូមជ្រើសរើសតំណែង"),         ref:positionRef,   empty:!form.positionId },
      { key:"departmentId", msg:t("សូមជ្រើសរើសនាយកដ្ឋាន"),     ref:departmentRef, empty:!form.departmentId },
      { key:"joinDate",     msg:t("សូមបញ្ចូលថ្ងៃចូលធ្វើការ"),  ref:joinDateRef,   empty:!form.joinDate },
      { key:"status",       msg:t("សូមជ្រើសរើសស្ថានភាព"),       ref:statusRef,     empty:!form.status },
      { key:"phone",        msg:t("សូមបញ្ចូលលេខទូរស័ព្ទ"),      ref:phoneRef,      empty:!form.phone?.trim() },
      { key:"email",        msg:t("សូមបញ្ចូលអ៊ីមែល"),            ref:emailRef,      empty:!form.email?.trim() },
    ];

    const reqErrors = {};
    requiredFields.forEach(fld => { if (fld.empty) reqErrors[fld.key] = fld.msg; });

    if (Object.keys(reqErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...reqErrors }));
      const first = requiredFields.find(fld => fld.empty && fld.ref?.current);
      if (first) setTimeout(() => first.ref.current.focus(), 80);
      return;
    }

        setSaving(true);
    try {
      const payload = {
        officerCode:    form.officerCode    || "",
        positionId:     form.positionId     ? parseInt(form.positionId,  10) : null,
        departmentId:   form.departmentId   ? parseInt(form.departmentId, 10) : null,
        ...(form.officeId ? { officeId: parseInt(form.officeId, 10) } : {}),
        fullNameKh:     form.fullNameKh     || "",
        fullNameEn:     form.fullNameEn     || "",
        gender:         form.gender         || "M",
        dob:            form.dob            || null,
        joinDate:       form.joinDate       || null,
        jobDescription: form.jobDescription || "",
        educationLevel: form.educationLevel || "",
        specialization: form.specialization || "",
        salaryGrade:    form.salaryGrade    || "",
        currentAddress: form.currentAddress || "",
        birthplace:     form.birthplace     || "",
        livingStatus:   form.livingStatus   || "",
        phone:          form.phone          || "",
        email:          form.email          || "",
        statusCode:         form.status         || "ACTIVE",
      };

      let officerId = editId;
      if (editId) {
        await officerAPI.update(editId, payload);
      } else {
        const res = await officerAPI.create(payload);
        const created = res?.data?.data ?? res?.data;
        officerId = created?.officerId ?? created?.id;
      }

      if (imageFile && officerId) {
        try {
          await officerAPI.uploadImage(officerId, imageFile);
        } catch (uploadErr) {
          console.warn("Profile image upload failed:", uploadErr.message);
          setErr("រក្សាទុកមន្ត្រីរួចហើយ ប៉ុន្តែការបញ្ចូលរូបភាពបរាជ័យ: " + errMessage(uploadErr));
        }
      }

      await load();
      setToast(editId ? "កែប្រែមន្ត្រីបានជោគជ័យ!" : "បន្ថែមមន្ត្រីបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
      setOriginalCode(""); setImageFile(null);
    } catch (e) {
      try {
        const body = JSON.parse(e.message);
        if (body?.data && typeof body.data === "object") {
          setErrors(body.data);
          setTimeout(() => {
            if (body.data.fullNameKh && nameKhRef.current) {
              nameKhRef.current.focus();
            } else if (body.data.dob) {
              document.querySelector('input[type="date"]')?.focus();
            } else if (body.data.phone) {
              document.querySelector('input[placeholder="0xx xxx xxx"]')?.focus();
            } else if (body.data.email) {
              document.querySelector('input[type="email"]')?.focus();
            }
          }, 80);
          return;
        }
      } catch {}
      setErrors({ _general: e.message || "Save failed" });

    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await officerAPI.delete(id);
      await load();
      if (getId(selected) === id) setModal(null);
      setToast(t("លុបមន្ត្រីបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  const handleStatusChange = async (officer, newStatus) => {
    try {
      const payload = {
        officerCode:    officer.officerCode    || "",
        positionId:     getPositionId(officer) ? parseInt(getPositionId(officer), 10) : null,
        departmentId:   getDeptId(officer)     ? parseInt(getDeptId(officer), 10)     : null,
        ...(getOfficeId(officer) ? { officeId: parseInt(getOfficeId(officer), 10) } : {}),
        fullNameKh:     officer.fullNameKh     || "",
        fullNameEn:     officer.fullNameEn     || "",
        gender:         officer.gender         || "M",
        dob:            officer.dob?.slice(0,10)      || null,
        joinDate:       officer.joinDate?.slice(0,10) || null,
        jobDescription: officer.jobDescription || "",
        educationLevel: officer.educationLevel || "",
        specialization: officer.specialization || "",
        salaryGrade:    officer.salaryGrade    || "",
        currentAddress: officer.currentAddress || "",
        birthplace:     officer.birthplace     || "",
        livingStatus:   officer.livingStatus   || "",
        phone:          officer.phone          || "",
        email:          officer.email          || "",
        statusCode:     newStatus,
      };
      await officerAPI.update(getId(officer), payload);
      await load();
      setToast(newStatus === "ACTIVE"
        ? "បានបើកដំណើរការមន្ត្រីជោគជ័យ!"
        : "បានផ្អាកដំណើរការមន្ត្រីជោគជ័យ!");
    } catch (e) { setErr(errMessage(e)); }
  };

  const openAdd = () => {
    setEditId(null); setOriginalCode("");
    setForm({ status:"ACTIVE", gender:"M" });
    setImageFile(null);
    setErrors({});
    setModal("add");
  };

  const openEdit = (s) => {
    const id = getId(s);
    setEditId(id);
    setOriginalCode(s.officerCode || "");
    setForm({
      officerCode:    s.officerCode    || "",
      positionId:     getPositionId(s) ? String(getPositionId(s)) : "",
      departmentId:   getDeptId(s)     ? String(getDeptId(s))    : "",
      officeId:       getOfficeId(s)   ? String(getOfficeId(s))  : "",
      fullNameKh:     s.fullNameKh     || "",
      fullNameEn:     s.fullNameEn     || "",
      gender:         s.gender         || "M",
      dob:            s.dob?.slice(0,10)      || "",
      joinDate:       s.joinDate?.slice(0,10) || "",
      jobDescription: s.jobDescription || "",
      educationLevel: s.educationLevel || "",
      specialization: s.specialization || "",
      salaryGrade:    s.salaryGrade    || "",
      currentAddress: s.currentAddress || "",
      birthplace:     s.birthplace     || "",
      livingStatus:   s.livingStatus   || "",
      phone:          s.phone          || "",
      email:          s.email          || "",
      status:         getStatus(s)     || "ACTIVE",
      imageSrc:       toImageUrl(s.profileImageUrl),
    });
    setImageFile(null);
    setErrors({});
    setModal("add");
    officerAPI.getProfileImage(id)
      .then(res => {
        const raw = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
        const url = toImageUrl(raw);
        if (url) setForm(prev => ({ ...prev, imageSrc: url }));
        else if (raw) console.warn("profile-image: unusable value from API:", raw);
      })
      .catch((e) => console.warn("profile-image request failed:", e.message));
  };

  const filtered = list.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.officerCode, s.fullNameKh, s.fullNameEn,
            getPositionName(s), getDeptName(s), getOfficeName(s)]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const positionsForDept = form.departmentId
    ? positions.filter(p =>
        String(getDeptId(p) ?? "") === String(form.departmentId))
    : [];
  const officesForDept = form.departmentId
    ? offices.filter(o =>
        String(getDeptId(o) ?? "") === String(form.departmentId))
    : [];

  return (
    <div>
      <style>{ERROR_STYLE}</style>
      <div style={{ display:"flex", gap:12, marginBottom:16,
                    alignItems:"center", flexWrap:"wrap" }}>
        <div data-tour="officer-search" style={{ flex:1, minWidth:220, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <span style={{ color:MUTED }}>
            <img
              src={require("../assets/search.png")}
              alt="Search"
              style={{
                width: "20px",
                height: "20px",
                objectFit: "contain",
              }}
        /></span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរក....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>

        <select data-tour="officer-status" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="ACTIVE">{t("កំពុងបម្រើការ")}</option>
          <option value="INACTIVE">{t("មិនសកម្ម")}</option>
          <option value="RETIRED">{t("ចូលនិវត្តន៍")}</option>
          <option value="SUSPENDED">{t("ផ្អាកការងារ")}</option>
          <option value="ON_LEAVE">{t("ឈប់សម្រាក")}</option>
          <option value="PROBATION">{t("សាកល្បង")}</option>
          <option value="RESIGNED">{t("បានលាឈប់")}</option>
          <option value="CONTRACT_EXPIRED">{t("កិច្ចសន្យាផុត")}</option>
        </select>

        <select data-tour="officer-dept" value={deptFilter}
          onChange={e => { setDeptFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("នាយកដ្ឋានទាំងអស់")}</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.departmentName}</option>
          ))}
        </select>

        <Can perm="officer" action="create">
        <button data-tour="officer-add" onClick={openAdd}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ បន្ថែមមន្ត្រី")}
        </button>
        </Can>
      </div>

      {!loading && (
        <p style={{ fontSize:12, color:MUTED, marginBottom:10, fontFamily:KH }}>
          {t("សរុប:")} <strong style={{ color:BROWN }}>{totalItems}</strong> {t("នាក់")}
        </p>
      )}

      <div data-tour="officer-table" style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខ")}</Th>
              <Th>{t("ភេទ")}</Th>
              <Th>{t("ឈ្មោះខ្មែរ")}</Th>
              <Th>{t("ឈ្មោះអង់គ្លេស")}</Th>
              <Th>{t("តំណែង")}</Th>
              <Th>{t("ការិយាល័យ")}</Th>
              <Th>{t("នាយកដ្ឋាន")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ថ្ងៃចូលការ")}</Th>
              <ThActions perm="officer" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5].map(i => <SkeletonRow key={i} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានទិន្នន័យ")}
                </td>
              </tr>
            ) : filtered.map(s => (
              <tr key={getId(s)}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{s.officerCode || getId(s)}</Td>
                <Td>{GENDER_LABEL[s.gender] || s.gender || "—"}</Td>
                <Td>{s.fullNameKh || "—"}</Td>
                <Td muted>{s.fullNameEn || "—"}</Td>
                <Td>{getPositionName(s) || "—"}</Td>
                <Td muted>{getOfficeName(s) || "—"}</Td>
                <Td>{getDeptName(s) || "—"}</Td>
                <Td>
                  <StatusBadge status={STATUS_LABEL[getStatus(s)] || getStatus(s)} />
                </Td>
                <Td muted>{toDisplay(s.joinDate)}</Td>
                <ActionBtns perm="officer"
                  onView={() => {
                    setSelected({ ...s, imageSrc: toImageUrl(s.profileImageUrl) });
                    setModal("view");
                    officerAPI.getProfileImage(getId(s))
                      .then(res => {
                        const raw = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
                        const url = toImageUrl(raw);
                        if (url) setSelected(prev => prev ? { ...prev, imageSrc: url } : prev);
                        else if (raw) console.warn("profile-image: unusable value from API:", raw);
                      })
                      .catch((e) => console.warn("profile-image request failed:", e.message));
                  }}
                  onEdit={() => openEdit(s)}
                  onDelete={() => handleDelete(getId(s))}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>

        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបមន្ត្រីនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <ConfirmDialog
        open={statusChange !== null}
        title={statusChange?.newStatus === "ACTIVE"
          ? "បញ្ជាក់ការបើកដំណើរការ" : "បញ្ជាក់ការផ្អាកដំណើរការ"}
        message={statusChange?.newStatus === "ACTIVE"
          ? `តើអ្នកពិតជាចង់បើកដំណើរការមន្ត្រី «${statusChange?.officer?.fullNameKh || ""}» មែនទេ?`
          : `តើអ្នកពិតជាចង់ផ្អាកដំណើរការមន្ត្រី «${statusChange?.officer?.fullNameKh || ""}» មែនទេ?`}
        warning={null}
        icon={statusChange?.newStatus === "ACTIVE"
          ? <MdCheckCircle size={22} color="#16A34A" />
          : <MdLock size={22} color="#D97706" />}
        iconBg={statusChange?.newStatus === "ACTIVE" ? "#DCFCE7" : "#FEF3C7"}
        confirmLabel={statusChange?.newStatus === "ACTIVE" ? "បើកដំណើរការ" : "ផ្អាកដំណើរការ"}
        confirmColor={statusChange?.newStatus === "ACTIVE" ? "#16A34A" : "#D97706"}
        onConfirm={() => {
          const { officer, newStatus } = statusChange;
          setStatusChange(null);
          setModal(null);
          handleStatusChange(officer, newStatus);
        }}
        onCancel={() => setStatusChange(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          key={editId || "new"}
          title={editId ? "កែប្រែព័ត៌មានមន្ត្រី" : "បង្កើតព័ត៌មានរបស់មន្ត្រី"}
          subtitle={t("បំពេញព័ត៌មានខាងក្រោម")}
          onClose={() => {
            setModal(null); setForm({}); setEditId(null);
            setOriginalCode("");
            setErrors({}); setImageFile(null);
          }}
        >
          <SectionTitle>{t("រូបភាពប្រវត្តិរូប")}</SectionTitle>
          <FieldBox>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:84, height:84, borderRadius:"50%", overflow:"hidden",
                            border:`2px solid ${BORDER}`, background:BG, flexShrink:0,
                            display:"flex", alignItems:"center", justifyContent:"center" }}>
                {imageFile
                  ? <img src={URL.createObjectURL(imageFile)} alt="preview"
                      style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  : form.imageSrc
                  ? <img src={form.imageSrc} alt="current"
                      onClick={() => setLightbox(form.imageSrc)}
                      style={{ width:"100%", height:"100%", objectFit:"cover", cursor:"zoom-in" }} />
                  : <MdPerson size={40} color={MUTED} />}
              </div>
              <div>
                <label style={{ display:"inline-block", padding:"8px 16px",
                                background:BG, border:`1px solid ${BORDER}`,
                                borderRadius:8, cursor:"pointer", fontSize:13,
                                color:BROWN, fontFamily:KH }}>
                  {t("ជ្រើសរើសរូបភាព")}
                  <input type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={e => setImageFile(e.target.files[0] || null)}
                    style={{ display:"none" }} />
                </label>
                {imageFile && (
                  <button type="button" onClick={() => setImageFile(null)}
                    style={{ marginLeft:8, background:"#FEE2E2", border:"none",
                             borderRadius:6, padding:"6px 12px", cursor:"pointer",
                             color:"#991B1B", fontSize:12, fontFamily:KH }}>
                    {t("លុប")}
                  </button>
                )}
                <p style={{ fontSize:11, color:MUTED, marginTop:6, fontFamily:KH }}>
                  {imageFile ? imageFile.name : "JPG, PNG, WEBP"}
                </p>
              </div>
            </div>
          </FieldBox>

          <SectionTitle>{t("ព័ត៌មានអត្តសញ្ញាណ")}</SectionTitle>
          <FieldBox>
            <Label required>{t("លេខកូដមន្ត្រី")}</Label>
            <input data-tour="officer-form-code" value={form.officerCode||""}
              onChange={e => f("officerCode",e.target.value)}
              placeholder={t("ឧ. OFC-001")} style={inputStyle} />
          </FieldBox>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FieldBox>
              <Label required>{t("ឈ្មោះពេញ ខ្មែរ")}</Label>
              <input
                ref={nameKhRef}
                value={form.fullNameKh||""}
                onChange={e => { f("fullNameKh",e.target.value); setErrors(p=>({...p,fullNameKh:undefined})); }}
                placeholder={t("ឧ. ចាន់ ដារ៉ា")}
                style={errStyle("fullNameKh", errors)} />
              <ErrMsg field="fullNameKh" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ឈ្មោះពេញ EN")}</Label>
              <input value={form.fullNameEn
              ||""}
                onChange={e => f("fullNameEn",e.target.value)}
                placeholder="e.g. Chan Dara" style={inputStyle} />
            </FieldBox>
            <FieldBox>
              <Label required>{t("ភេទ")}</Label>
              <select
                ref={genderRef}
                value={form.gender||"M"}
                onChange={e => { f("gender",e.target.value); setErrors(p=>({...p,gender:undefined})); }}
                style={errStyle("gender", errors)}>
                <option value="M">{t("ប្រុស")}</option>
                <option value="F">{t("ស្រី")}</option>
                <option value="MONK">{t("បព្វជិត")}</option>
              </select>
              <ErrMsg field="gender" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ថ្ងៃខែឆ្នាំកំណើត")}</Label>
              <input type="date" value={form.dob||""}
                onChange={e => { f("dob",e.target.value); setErrors(p=>({...p,dob:undefined})); }}
                style={{ ...inputStyle, borderColor: errors.dob ? "#EF4444" : undefined }} />
              <ErrMsg field="dob" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ទីកន្លែងកំណើត")}</Label>
              <input value={form.birthplace||""}
                onChange={e => f("birthplace",e.target.value)}
                placeholder={t("ខេត្ត/ក្រុង")} style={inputStyle} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ស្ថានភាពស្នាក់នៅ")}</Label>
              <input value={form.livingStatus||""}
                onChange={e => f("livingStatus",e.target.value)}
                placeholder={t("ជួល / ផ្ទាល់ខ្លួន")} style={inputStyle} />
            </FieldBox>
          </div>

          <SectionTitle>{t("ព័ត៌មានការងារ")}</SectionTitle>
          <div data-tour="officer-form-work" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FieldBox>
              <Label required>{t("នាយកដ្ឋាន")}</Label>
              <select ref={departmentRef}
                value={form.departmentId||""}
                onChange={e => {
                  const dept = e.target.value;
                  setForm(p => ({ ...p, departmentId: dept, positionId: "", officeId: "" }));
                  setErrors(p => ({ ...p, departmentId: undefined }));
                }}
                style={errStyle("departmentId", errors)}>
                <option value="">{t("ជ្រើសរើសនាយកដ្ឋាន...")}</option>
                {departments.map(d => (
                  <option key={d.id} value={String(d.id)}>{d.departmentName}</option>
                ))}
              </select>
              <ErrMsg field="departmentId" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ការិយាល័យ")}</Label>
              <select ref={officeRef}
                value={form.officeId||""}
                disabled={!form.departmentId}
                onChange={e => { f("officeId",e.target.value); setErrors(p=>({...p,officeId:undefined})); }}
                style={{ ...errStyle("officeId", errors),
                         ...(!form.departmentId ? { background:"#F5F5F5", cursor:"not-allowed" } : {}) }}>
                <option value="">
                  {!form.departmentId
                    ? "សូមជ្រើសរើសនាយកដ្ឋានជាមុនសិន..."
                    : officesForDept.length === 0
                    ? "គ្មានការិយាល័យក្នុងនាយកដ្ឋាននេះ"
                    : "ជ្រើសរើសការិយាល័យ..."}
                </option>
                {officesForDept.map(o => (
                  <option key={o.id} value={String(o.id)}>{o.officeName}</option>
                ))}
              </select>
              <ErrMsg field="officeId" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label required>{t("តំណែង")}</Label>
              <select ref={positionRef}
                value={form.positionId||""}
                disabled={!form.departmentId}
                onChange={e => { f("positionId",e.target.value); setErrors(p=>({...p,positionId:undefined})); }}
                style={{ ...errStyle("positionId", errors),
                         ...(!form.departmentId ? { background:"#F5F5F5", cursor:"not-allowed" } : {}) }}>
                <option value="">
                  {!form.departmentId
                    ? "សូមជ្រើសរើសនាយកដ្ឋានជាមុនសិន..."
                    : positionsForDept.length === 0
                    ? "គ្មានតំណែងក្នុងនាយកដ្ឋាននេះ"
                    : "ជ្រើសរើសតំណែង..."}
                </option>
                {positionsForDept.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.positionName}</option>
                ))}
              </select>
              <ErrMsg field="positionId" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label required>{t("ថ្ងៃចូលធ្វើការ")}</Label>
              <input type="date" ref={joinDateRef}
                value={form.joinDate||""}
                onChange={e => { f("joinDate",e.target.value); setErrors(p=>({...p,joinDate:undefined})); }}
                style={errStyle("joinDate", errors)} />
              <ErrMsg field="joinDate" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ឋានន្តរស័ក្តិ")}</Label>
              <input value={form.salaryGrade||""}
                onChange={e => f("salaryGrade",e.target.value)}
                placeholder="A1, B2..." style={inputStyle} />
            </FieldBox>
            <FieldBox>
              <Label>{t("កម្រិតវប្បធម៌")}</Label>
              <input value={form.educationLevel||""}
                onChange={e => f("educationLevel",e.target.value)}
                placeholder={t("បរិញ្ញាបត្រ...")} style={inputStyle} />
            </FieldBox>
            <FieldBox>
              <Label>{t("ឯកទេស")}</Label>
              <input value={form.specialization||""}
                onChange={e => f("specialization",e.target.value)}
                placeholder="Computer Science..." style={inputStyle} />
            </FieldBox>
            <FieldBox>
              <Label required>{t("ស្ថានភាព")}</Label>
              <select
                ref={statusRef}
                value={form.status||"ACTIVE"}
                onChange={e => { f("status",e.target.value); setErrors(p=>({...p,status:undefined})); }}
                style={errStyle("status", errors)}>
                <option value="ACTIVE">{t("កំពុងបម្រើការ")}</option>
                <option value="INACTIVE">{t("មិនសកម្ម")}</option>
                <option value="RETIRED">{t("ចូលនិវត្តន៍")}</option>
                <option value="SUSPENDED">{t("ផ្អាកការងារ")}</option>
                <option value="ON_LEAVE">{t("ឈប់សម្រាក")}</option>
                <option value="PROBATION">{t("សាកល្បង")}</option>
                <option value="RESIGNED">{t("បានលាឈប់")}</option>
                <option value="CONTRACT_EXPIRED">{t("កិច្ចសន្យាផុត")}</option>
              </select>
            </FieldBox>
          </div>
          <FieldBox>
            <Label>{t("មុខងារការងារ")}</Label>
            <textarea value={form.jobDescription||""}
              onChange={e => f("jobDescription",e.target.value)}
              rows={2} placeholder={t("ពិពណ៌នាអំពីភារកិច្ច...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </FieldBox>

          <SectionTitle>{t("ព័ត៌មានទំនាក់ទំនង")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FieldBox>
              <Label required>{t("ទូរស័ព្ទ")}</Label>
              <input ref={phoneRef}
                value={form.phone||""}
                onChange={e => { f("phone",e.target.value); setErrors(p=>({...p,phone:undefined})); }}
                placeholder="0xx xxx xxx"
                style={errStyle("phone", errors)} />
              <ErrMsg field="phone" errors={errors} />
            </FieldBox>
            <FieldBox>
              <Label required>{t("អ៊ីមែល")}</Label>
              <input type="email" ref={emailRef}
                value={form.email||""}
                onChange={e => { f("email",e.target.value); setErrors(p=>({...p,email:undefined})); }}
                placeholder="example@email.com"
                style={errStyle("email", errors)} />
              <ErrMsg field="email" errors={errors} />
            </FieldBox>
          </div>
          <FieldBox>
            <Label>{t("អាសយដ្ឋានបច្ចុប្បន្ន")}</Label>
            <input value={form.currentAddress||""}
              onChange={e => f("currentAddress",e.target.value)}
              placeholder={t("លេខ ផ្លូវ ភូមិ ឃុំ ស្រុក ខេត្ត")}
              style={inputStyle} />
          </FieldBox>

          <div data-tour="officer-form-actions" style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => {
                setModal(null); setForm({}); setEditId(null);
                setOriginalCode("");
                setErrors({}); setImageFile(null);
              }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.fullNameKh || "មន្ត្រី"}
          subtitle={selected.fullNameEn || ""}
          onClose={() => setModal(null)}
        >
          <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
            <div style={{ position:"relative" }}>
              {selected.imageSrc ? (
                <>
                  <img src={selected.imageSrc} alt={selected.fullNameKh || "officer"}
                    onClick={() => setLightbox(selected.imageSrc)}
                    title={t("ចុចដើម្បីមើលរូបពេញ")}
                    style={{ width:130, height:130, borderRadius:"50%",
                             objectFit:"cover", border:`3px solid ${BORDER}`,
                             cursor:"zoom-in" }} />
                  
                </>
              ) : (
                <div style={{ width:130, height:130, borderRadius:"50%",
                              background:BG, border:`3px solid ${BORDER}`,
                              display:"flex", alignItems:"center",
                              justifyContent:"center" }}>
                  <MdPerson size={56} color={MUTED} />
                </div>
              )}
            </div>
          </div>

          <SectionTitle>{t("ព័ត៌មានអត្តសញ្ញាណ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdBadge size={16} />}        label={t("លេខកូដមន្ត្រី")}      value={selected.officerCode || getId(selected)} />
            <InfoRow icon={<MdPerson size={16} />}       label={t("ភេទ")}                value={GENDER_LABEL[selected.gender] || selected.gender} />
            <InfoRow icon={<MdPerson size={16} />}       label={t("ឈ្មោះពេញ ខ្មែរ")}     value={selected.fullNameKh} />
            <InfoRow icon={<MdPerson size={16} />}       label={t("ឈ្មោះពេញ EN")}        value={selected.fullNameEn} />
            <InfoRow icon={<MdCake size={16} />}         label={t("ថ្ងៃខែឆ្នាំកំណើត")}   value={toDisplay(selected.dob)} />
            <InfoRow icon={<MdLocationOn size={16} />}   label={t("ទីកន្លែងកំណើត")}      value={selected.birthplace} />
            <InfoRow icon={<MdHome size={16} />}         label={t("ស្ថានភាពស្នាក់នៅ")}   value={selected.livingStatus} />
          </div>

          <SectionTitle>{t("ព័ត៌មានការងារ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccountBalance size={16} />} label={t("នាយកដ្ឋាន")}        value={getDeptName(selected)} />
            <InfoRow icon={<MdWork size={16} />}          label={t("ការិយាល័យ")}          value={getOfficeName(selected)} />
            <InfoRow icon={<MdWork size={16} />}          label={t("តំណែង")}             value={getPositionName(selected)} />
            <InfoRow icon={<MdCalendarToday size={16} />}  label={t("ថ្ងៃចូលធ្វើការ")}    value={toDisplay(selected.joinDate)} />
            <InfoRow icon={<MdAttachMoney size={16} />}    label={t("ឋានន្តរស័ក្តិ")}      value={selected.salaryGrade} />
            <InfoRow icon={<MdSchool size={16} />}         label={t("កម្រិតវប្បធម៌")}      value={selected.educationLevel} />
            <InfoRow icon={<MdScience size={16} />}        label={t("ឯកទេស")}             value={selected.specialization} />
          </div>
          <InfoRow icon={<MdAssignment size={16} />} label={t("ស្ថានភាព")}
            value={<StatusBadge status={STATUS_LABEL[getStatus(selected)] || getStatus(selected)} />} />
          {selected.jobDescription && (
            <InfoRow icon={<MdNotes size={16} />} label={t("មុខងារការងារ")} value={selected.jobDescription} />
          )}

          <SectionTitle>{t("ព័ត៌មានទំនាក់ទំនង")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdPhone size={16} />}  label={t("ទូរស័ព្ទ")} value={selected.phone} />
            <InfoRow icon={<MdEmail size={16} />}  label={t("អ៊ីមែល")} value={selected.email} />
          </div>
          <InfoRow icon={<MdHome size={16} />} label={t("អាសយដ្ឋានបច្ចុប្បន្ន")} value={selected.currentAddress} />

          <SectionTitle>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccessTime size={16} />} label={t("ថ្ងៃបង្កើត")} value={toDisplay(selected.createdAt)} />
            <InfoRow icon={<MdEdit size={16} />}        label={t("ថ្ងៃកែប្រែ")} value={toDisplay(selected.updatedAt)} />
          </div>

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
            <div style={{ display:"flex", gap:8 }}>
              <Can perm="officer" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)}
                style={{ display:"flex", alignItems:"center", gap:5 }}>
                <MdEdit size={14} /> {t("កែប្រែ")}
              </Btn>
              </Can>
              {getStatus(selected) === "ACTIVE"
                ? <Btn variant="outline"
                    onClick={() => setStatusChange({ officer:selected, newStatus:"INACTIVE" })}
                    style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <MdLock size={14} /> {t("ផ្អាកដំណើរការ")}
                  </Btn>
                : <Btn variant="outline"
                    onClick={() => setStatusChange({ officer:selected, newStatus:"ACTIVE" })}
                    style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <MdCheckCircle size={14} /> {t("បើកដំណើរការ")}
                  </Btn>
              }
            </div>
            <Can perm="officer" action="delete">
            <Btn variant="danger"
              onClick={() => handleDelete(getId(selected))}
              style={{ display:"flex", alignItems:"center", gap:5 }}>
              <MdDelete size={14} /> {t("លុប")}
            </Btn>
            </Can>
          </div>
        </Modal>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position:"fixed", inset:0, zIndex:9999,
                   background:"rgba(0,0,0,0.85)", display:"flex",
                   alignItems:"center", justifyContent:"center",
                   padding:24, cursor:"zoom-out" }}>
          <button onClick={() => setLightbox(null)}
            style={{ position:"absolute", top:18, right:22, background:"none",
                     border:"none", color:"#fff", fontSize:34, cursor:"pointer",
                     lineHeight:1 }}>×</button>
          <img src={lightbox} alt="officer full"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth:"95%", maxHeight:"95%", objectFit:"contain",
                     borderRadius:8, boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }} />
        </div>
      )}
    </div>
  );
}